<script setup lang="ts">
import {
    SupportedActionsCartControlEnum,
    SupportedVariantsCartControlEnum,
    type SupportedActionsCartControl,
    type SupportedVariantsCartControl,
} from "~/types/CartControl"
import Loader from "~/components/Loader.vue"
/**
 *
 *  Props and emits section
 *
 *
 */

const props = defineProps<{
    variant: SupportedVariantsCartControl
    disabledColor?: boolean
    withoutShopButton?: boolean
    minValue?: number
    maxValue?: number
    initialValue?: number
    loading?: boolean
    colorLoading?: string
}>()

const emits = defineEmits<{
    (e: "onClickCart", amount: number): void
    (e: "onUpdateAmount", amount: number): void
    (e: "onValidateSubmit"): boolean
}>()

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */
const minValue = ref()
const maxValue = ref()
const productAmount = ref()
const error = ref()

/**
 *
 *  Life cicle events
 *
 *
 */

onMounted(() => {
    minValue.value = generateMinValue()
    productAmount.value = generateInitialValue()
    maxValue.value = generateMaxValue()
})

/**
 *
 *  Functions section
 *
 *
 */

const generateMinValue = () => {
    if (props.minValue && props.minValue >= 0) return props.minValue
    return 0
}

const generateMaxValue = () => {
    if (
        props.maxValue &&
        props.maxValue > minValue.value &&
        props.maxValue > productAmount.value
    )
        return props.maxValue
    return null
}

const generateInitialValue = () => {
    if (
        props.initialValue &&
        props.minValue &&
        props.initialValue > props.minValue
    )
        return props.initialValue
    if (props.initialValue && !props.minValue) return props.initialValue
    if (!props.initialValue && props.minValue && props.minValue > 0)
        return props.minValue
    return 1
}

const getVariantStyles = () => {
    if (props.variant === SupportedVariantsCartControlEnum.primary) {
        return "tw-bg-primary tw-text-white tw-border tw-border-primary tw-px-8"
    }

    if (props.variant === SupportedVariantsCartControlEnum.secondary) {
        return `tw-bg-transparent ${props.disabledColor ? "tw-text-gray tw-border-gray" : "tw-text-primary tw-border-primary hover:tw-bg-primary hover:tw-text-white hover:tw-border-none"} tw-border`
    }
}

const onClickCart = () => {
    if(validateAmount())
        emits("onClickCart", productAmount.value)
    // productAmount.value = minValue.value
}

const updateAmount = (action: SupportedActionsCartControl) => {
    if (action === SupportedActionsCartControlEnum.increment) {
        if (
            props.maxValue &&
            maxValue.value &&
            productAmount.value === maxValue.value &&
            !validateAmount()
        )
            return 
        productAmount.value++
    }
    if (action === SupportedActionsCartControlEnum.decrement) {
        if (productAmount.value === minValue.value && !validateAmount()) return 
        productAmount.value--
        
    }   
        if(validateAmount())
            emits("onUpdateAmount", productAmount.value)
}

const validateAction = () => {
    const defaultValidateAction = false
    validateAmount()
    return emits("onValidateSubmit") || defaultValidateAction 
}

function validateAmount() {
    if(productAmount.value >= minValue.value ||  /^\d+$/.test(productAmount.value)){
        error.value=''
        emits("onUpdateAmount", productAmount.value)
        return true
    }
    else{
        error.value='Ingrese un número válido'
        return false
    }
        
}

defineExpose({
    productAmount,
})
</script>

<template>
    <div class="tw-flex tw-flex-col tw-mt-4">
        <span  class="tw-text-red-500 tw-text-xs " v-if="error">{{error}}</span>
        <div class="tw-flex tw-justify-between tw-gap-4  tw-px-0">
            <div
                class="tw-flex tw-w-full tw-max-w-[110px] tw-h-11 tw-border tw-rounded-full tw-border-gray-200"
            >
                <button
                    :class="`${disabledColor ? 'tw-text-gray' : 'tw-text-primary'} tw-font-bold tw-bg-transparent tw-p-2`"
                    :disabled="productAmount === minValue || loading"
                    @click="updateAmount('decrement')"
                >
                    <q-icon name="fa fa-minus" size="10px" />
                </button>
                <div
                    class="tw-w-full tw-border-x tw-overflow-x-auto tw-border-gray-200 tw-p-0 tw-m-0"
                >
                    <input
                        :class="`tw-bg-transparent tw-min-w-[40px] sm:tw-min-w-[50px] tw-w-full tw-h-full tw-text-center ${disabledColor ? 'tw-text-gray' : 'tw-text-primary'}`"
                        v-model="productAmount"
                        @input = "validateAmount"
                    />

                </div>
                
                <button
                    :class="`${disabledColor ? 'tw-text-gray' : 'tw-text-primary'} tw-text-primary tw-font-bold tw-bg-transparent tw-p-2`"
                    :disabled="productAmount === maxValue || loading"
                    @click="updateAmount('increment')"
                >
                    <q-icon name="fa fa-plus" size="10px" />
                </button>
            </div>
            
            <button
                v-if="!withoutShopButton"
                @click="onClickCart"
                :class="`${getVariantStyles()} tw-cursor-pointer tw-rounded-full tw-w-full tw-min-w-[50px] tw-max-w-[110px] tw-flex tw-justify-center tw-items-center`"
                :disabled="validateAction()"
            >
                <Loader v-if="loading" :color="colorLoading || 'white'" />
                <q-icon v-else name="fa-solid fa-shopping-cart" size="17px" />
            </button>
        </div>
        
    </div>
</template>
